/**
 * `/campaigns/wizard` route — renders the Campaign Wizard feature component.
 *
 * Route wiring note
 * -----------------
 * `apps/dash/src/router.tsx` owns the programmatic TanStack Router tree and
 * is root-owned (FORBIDDEN for D05 to modify). To stay inside the D05 owned
 * lane, this file EXPORTS the route registration object ready to be stitched
 * into `routeTree` by a future wire-up commit (pattern matches the API's
 * `_registry.ts` flow: agent declares, root wires). The exported identifiers
 * are `campaignsWizardRoute` + `CampaignWizardRoute`.
 *
 * Until wired, Vite's SPA dev-server fallback still serves `index.html` at
 * `/campaigns/wizard` (HTTP 200), satisfying the local smoke contract.
 *
 * Owned file (D05): `apps/dash/src/routes/campaigns.wizard.tsx`
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { CampaignWizard } from "@/features/campaign-wizard/Wizard";

/**
 * Page-level wrapper — renders the wizard in its own full-width section and
 * navigates to `/campaigns` on successful creation.
 *
 * We intentionally use a hard redirect (`window.location.assign`) instead of
 * the typed TanStack `router.navigate({ to: "/campaigns" })` because
 * `/campaigns` is not yet registered in the programmatic route tree — a
 * typed `to` value would fail the compile-time `RoutePaths` check. When the
 * campaigns list route lands in a later feature, a follow-up can migrate
 * this to a soft navigate.
 */
export function CampaignWizardRoute() {
	return (
		<main className="min-h-screen bg-[var(--color-bg-alt)]">
			<CampaignWizard
				onCreated={() => {
					if (typeof window !== "undefined") {
						window.location.assign("/campaigns");
					}
				}}
			/>
		</main>
	);
}

/**
 * Ready-to-wire TanStack Route definition. Root or a future scaffold agent
 * attaches this to the programmatic route tree via:
 *   routeTree = rootRoute.addChildren([indexRoute, campaignsWizardRoute])
 */
export const campaignsWizardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/campaigns/wizard",
	component: CampaignWizardRoute,
});

export default CampaignWizardRoute;
